/// <amd-module name="VSS/Features/Charts/Internal/DefaultColorDictionary" />

import * as Chart_Contracts from "VSS/Features/Charts/Contracts";
import { ColorUtilities } from "VSS/Features/Charts/Internal/ColorUtilities";
import * as Charts_Resources from "VSS/Features/Charts/Resources";

export class DefaultFeatureColorProvider implements Chart_Contracts.FeatureColorProvider {
    public getFeatureColor(key: string, isSubdued: boolean = false): Chart_Contracts.ColorPair {
        let backgroundColor = null;
        if (key === Charts_Resources.ChartsRemainingItems) {
            // Gray color used for the "(other)" series that is unique and not in the color palette
            backgroundColor = isSubdued ? "#C8C5C6" : "#959595";
        }

        if (backgroundColor != null) {
            return {
                background: backgroundColor,
                foreground: ColorUtilities.selectForegroundColor(backgroundColor)
            };
        }

        // By default, we return no color info, that's perfectly fine.
        return null;
    }
}

export class DefaultColorDictionary implements Chart_Contracts.ColorDictionary {
    /**
     * A js dictionary containing colors supplied by the user, associated to a specific chart.
     * User colors take precedence over all other colorings.
     */
    protected _customColorDictionary: { [key: string]: Chart_Contracts.ColorPair } = {};

    /** Provides color overrides beyond defaults, for any special cases by the feature providers. */
    protected _featureColorProvider: Chart_Contracts.FeatureColorProvider;

    constructor(
        userColors?: Chart_Contracts.ColorEntry[],
        featureColorProvider?: Chart_Contracts.FeatureColorProvider,
        palette: ColorSetBase = V2PaletteColors.getInstance()
    ) {
        this._featureColorProvider = featureColorProvider == null ? new DefaultFeatureColorProvider() : featureColorProvider;

        this._mergeUserColors(palette, userColors);
    }

    /**
     * Serialize color customizations to store with chart configuration.
     */
    public toColorEntries(): Chart_Contracts.ColorEntry[] {
        let result: Chart_Contracts.ColorEntry[] = null,
            value;

        if (Object.keys(this._customColorDictionary).length > 0) {
            result = new Array();
            for (value in this._customColorDictionary) {
                if (this._customColorDictionary.hasOwnProperty(value)) {
                    result.push({
                        value: value,
                        backgroundColor: this._customColorDictionary[value].background
                    });
                }
            }
        }
        return result;
    }

    /**
     * If a custom coloring exists for the key, the appropriate custom color will be provided.
     * Otherwise a default color is supplied based on the index.
     * @param key The key to search for
     * @param colorIndex The index of the color in the default color set to use if no custom color was found
     * @param isSubduedPalette Whether or not to use the subdued default color set
     */
    public getColorPair(key: string, colorIndex: number, isSubduedPalette?: boolean): Chart_Contracts.ColorPair {
        let color = this._getCustomColorPair(key);

        // If we didn't find a user provided color, look in the feature custom colors
        if (!color) {
            color = this._featureColorProvider.getFeatureColor(key, isSubduedPalette);
        }

        // If we didn't find a feature-specific color, use the default color for the given index
        if (!color) {
            color = this._getDefaultColorPair(colorIndex, isSubduedPalette);
        }

        return color;
    }

    /** Allows the caller to add color customization for the specified color */
    public setColorPair(key: string, colorPair: Chart_Contracts.ColorPair): void {
        this._customColorDictionary[key] = colorPair;
    }

    /** Indicates if a user-customized Color Pair is associated with the requested key */
    public hasCustomColorPair(key: string): boolean {
        return !!this._customColorDictionary[key];
    }

    /** Indicates if any user-customizations of color pairs are present. */
    public hasCustomColors(): boolean {
        return !(Object.keys(this._customColorDictionary).length === 0);
    }

    public clearCustomColors() {
        this._customColorDictionary = {};
    }

    private _getDefaultColorPair(colorIndex: number, isSubduedPalette?: boolean): Chart_Contracts.ColorPair {
        return isSubduedPalette
            ? V2DefaultSubduedColors.getInstance().getColorPair(colorIndex)
            : V2DefaultColors.getInstance().getColorPair(colorIndex);
    }

    private _getCustomColorPair(key: string) {
        return this._customColorDictionary[key];
    }

    /** Encapsulates logic for applying persisted user colors into a new Color dictionary.*/
    private _mergeUserColors(palette: ColorSetBase, userColors?: Chart_Contracts.ColorEntry[]) {
        if (userColors) {
            for (const color of userColors) {
                const colorPair = palette.getColorPairOfBackground(color.backgroundColor);
                this.setColorPair(color.value, colorPair);
            }
        }
    }
}

export abstract class ColorSetBase {
    protected get backgrounds(): string[] {
        return this._backgrounds.slice();
    }
    protected set backgrounds(backgroundColors: string[]) {
        this._backgrounds = ColorSetBase.toUpperCase(backgroundColors);
    }

    private static toUpperCase(colors: string[]): string[] {
        return colors.map((o: string) => o.toUpperCase());
    }
    private _backgrounds: string[];

    protected constructor() {
        this.backgrounds = [];
    }

    /** Note: This implementation will loop over if the requested index exceeds palette size.*/
    public getColorPair(i: number): Chart_Contracts.ColorPair {
        i = i % this.backgrounds.length;
        const background = this.backgrounds[i];
        return this.getColorPairOfBackground(background);
    }

    /*
     * Returns a foreground/background color pair for the supplied background color.
     * This implementation is tolerant of hex values unrecognized in the set of background colors.
     */
    public getColorPairOfBackground(background: string): Chart_Contracts.ColorPair {
        const foreground = ColorUtilities.selectForegroundColor(background); // Select a black or white foreground color depending on the background color.
        return {
            background: background,
            foreground: foreground
        };
    }
}

export class V2PaletteColors extends ColorSetBase {
    private static _instance: V2PaletteColors = null;
    public static getInstance(): V2PaletteColors {
        if (this._instance == null) {
            this._instance = new V2PaletteColors();
        }
        return this._instance;
    }

    protected constructor() {
        super();
        this.backgrounds = [
            "#222222",
            "#292E6B",
            "#009CCC",
            "#00643A",
            "#339947",
            "#FBBC3D",
            "#DB552C",
            "#7F1725",
            "#EC008C",
            "#5C197B",
            "#51399F",
            "#444444",
            "#1B478B",
            "#43B4D5",
            "#207752",
            "#60AF49",
            "#FBD144",
            "#E87025",
            "#B20B1E",
            "#EF33A3",
            "#71338D",
            "#6951AA",
            "#666666",
            "#0D60AB",
            "#86CDDE",
            "#56987D",
            "#8DC54B",
            "#FBE74B",
            "#F58B1F",
            "#E60017",
            "#F266BA",
            "#9260A1",
            "#8874C2",
            "#888888",
            "#007ACC",
            "#C9E7E7",
            "#7CAF9A",
            "#A8CE4B",
            "#FBFD52",
            "#F7A24B",
            "#EB3345",
            "#F599D1",
            "#AE88B9",
            "#AA9CDF",
            "#AAAAAA",
            "#3F9BD8",
            "#D6EDED",
            "#9CC3B2",
            "#C3D84C",
            "#FCFD7D",
            "#F9B978",
            "#F06673",
            "#F9CCE8",
            "#C7ABD0",
            "#C0B6E9",
            "#CCCCCC",
            "#7FBCE5",
            "#E4F3F3",
            "#BFD8CD",
            "#D7E587",
            "#FCFEA8",
            "#FBD0A5",
            "#F599A2",
            "#FBDDEF",
            "#E0CAE7",
            "#DAD4F7"
        ];
    }
}

/** Encapsulates the set of default colors */
export class V2DefaultColors extends ColorSetBase {
    private static _instance: V2DefaultColors = null;
    public static getInstance(): V2DefaultColors {
        if (this._instance == null) {
            this._instance = new V2DefaultColors();
        }
        return this._instance;
    }

    protected constructor() {
        super();

        this.backgrounds = [
            "#009CCC",
            "#F58B1F",
            "#CCCCCC",
            "#FBBC3D",
            "#007ACC",
            "#71338D",
            "#A8CE4B",
            "#888888",
            "#444444",
            "#F06673",
            "#56987D",
            "#86CDDE",
            "#FBE74B",
            "#AE88B9",
            "#B20B1E",
            "#D7E587",
            "#DB552C",
            "#7FBCE5",
            "#EC008C",
            "#00643A"
        ];
    }
}

/** Encapsulates the set of V2 default subdued colors for enhanced readability. */
export class V2DefaultSubduedColors extends ColorSetBase {
    private static _instance: V2DefaultSubduedColors = null;
    public static getInstance(): V2DefaultSubduedColors {
        if (this._instance == null) {
            this._instance = new V2DefaultSubduedColors();
        }
        return this._instance;
    }

    constructor() {
        super();

        this.backgrounds = ["#E0CAE7", "#C0B6E9", "#E4F3F3", "#F9CCE8", "#CCCCCC", "#FBDDEF", "#C9E7E7", "#DAD4F7", "#F599A2"];
    }
}
